import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBreadcrumbs } from '@mono/ui-components';
import { Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { appCrumbs } from '../apps/registry';
// Narrow adapter: SpecraBreadcrumbs' linkComponent contract types `to` as a
// plain string, while the router Link accepts the wider To union.
function RouterLink({ to, ...rest }) {
    return /*#__PURE__*/ _jsx(Link, {
        to: to,
        ...rest
    });
}
export function Breadcrumbs({ items, className }) {
    const location = useLocation();
    // Generate breadcrumbs from pathname if no items provided
    const breadcrumbItems = items || generateBreadcrumbsFromPath(location.pathname);
    return /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
        className: className,
        // Router Link keeps navigation client-side; SpecraBreadcrumbs' default
        // raw anchor would full-page reload the SPA.
        linkComponent: RouterLink,
        items: breadcrumbItems.map((item, index)=>({
                id: `${item.label}-${index}`,
                label: item.icon ? /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-1",
                    children: [
                        item.icon,
                        item.label
                    ]
                }) : item.label,
                href: item.href,
                current: index === breadcrumbItems.length - 1
            }))
    });
}
// Segments that are internal namespaces or default landings — never shown in
// breadcrumbs because they add noise without information.
const HIDDEN_SEGMENTS = new Set([
    'v2',
    'home'
]);
// Regex matching opaque IDs that should be hidden (UUIDs, project IDs like
// "proj_1775150502294_1pclc363", long random slugs, etc.).
function isOpaqueId(segment) {
    // UUID-ish
    if (/^[a-f0-9-]{8,}$/i.test(segment) && segment.includes('-')) return true;
    // Has the typical "proj_*", "project_*", "prj_*" prefix
    if (/^(proj|project|prj)_/i.test(segment)) return true;
    // Long mixed alphanumeric with underscores or digits — looks like an opaque ID
    if (segment.length > 16 && /[0-9]/.test(segment) && /[a-zA-Z]/.test(segment)) return true;
    return false;
}
function generateBreadcrumbsFromPath(pathname) {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    // Always start with home
    breadcrumbs.push({
        label: 'Home',
        href: '/',
        icon: /*#__PURE__*/ _jsx(Home, {
            className: "h-3 w-3"
        })
    });
    let currentPath = '';
    segments.forEach((segment, index)=>{
        currentPath += `/${segment}`;
        // Skip the dashboard segment as it's the home
        if (segment === 'dashboard' && index === 0) {
            return;
        }
        // Skip internal namespaces (v2) and default index pages (home).
        if (HIDDEN_SEGMENTS.has(segment.toLowerCase())) {
            return;
        }
        // Skip opaque IDs — they add no value and blow out the breadcrumb width.
        if (isOpaqueId(segment)) {
            return;
        }
        // Handle special cases
        if (segment === 'proposal') {
            // The next segment is the opaque proposal ID, which is hidden from the
            // breadcrumb display but is needed to build a link to that proposal's
            // home. Without this branch the accumulated path is "/proposal", which
            // has no route and 404s. Fall back to /projects if no ID is present
            // (bare /proposal URL), so the link still goes somewhere useful.
            const proposalId = segments[index + 1];
            breadcrumbs.push({
                label: 'Proposal',
                href: proposalId ? `/proposal/${proposalId}/v2/home` : '/projects'
            });
        } else if (segment === 'workflows') {
            breadcrumbs.push({
                label: 'Workflows',
                href: currentPath
            });
        } else if (segment === 'documents') {
            breadcrumbs.push({
                label: 'Documents',
                href: currentPath
            });
        } else if (segment === 'library') {
            breadcrumbs.push({
                label: 'Library',
                href: currentPath
            });
        } else if (segment === 'settings') {
            breadcrumbs.push({
                label: 'Settings',
                href: currentPath
            });
        } else if (segment === 'help') {
            breadcrumbs.push({
                label: 'Help',
                href: currentPath
            });
        } else if (segment === 'history') {
            breadcrumbs.push({
                label: 'History',
                href: currentPath
            });
        } else if (appCrumbs[segment]) {
            breadcrumbs.push({
                label: appCrumbs[segment],
                href: currentPath
            });
        } else if (segment === 'research-assistant') {
            breadcrumbs.push({
                label: 'Research Assistant',
                href: currentPath
            });
        } else {
            // For dynamic segments or unknown routes
            if (index === segments.length - 1) {
                // Last segment - don't make it clickable
                breadcrumbs.push({
                    label: getSegmentLabel(segment)
                });
            } else {
                breadcrumbs.push({
                    label: getSegmentLabel(segment),
                    href: currentPath
                });
            }
        }
    });
    return breadcrumbs;
}
function getSegmentLabel(segment) {
    // Handle dynamic segments
    if (segment.match(/^[a-f0-9-]+$/)) {
        // Looks like an ID - show a generic label
        return 'Details';
    }
    // Capitalize and format segment names
    return segment.split('-').map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}
